import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import {
  configureCollectJs,
  resetCollectJsConfiguration,
  waitForCollectJs,
} from './collect';
import { THREE_DS_MOUNT_ID } from './three-ds';

import classes from './styles.module.scss';

const FIELD_IDS = {
  ccnumber: 'tailoredpay-ccnumber',
  ccexp: 'tailoredpay-ccexp',
  cvv: 'tailoredpay-cvv',
};

type Placeholders = {
  ccnumber?: string;
  ccexp?: string;
  cvv?: string;
};

type Props = {
  className?: string;
  placeholders?: Placeholders;
  /** Amount charged today + its currency — needed by Collect.js at configure time. */
  price?: string | number;
  currency?: string;
  country?: string;
  /**
   * Apple Pay field config (see ./apple-pay). The button is mounted by Collect.js
   * into the selector this carries, which lives outside this component — the
   * payment modal owns that slot for every provider.
   */
  applePay?: Record<string, any> | null;
  onChange?: (e: { valid: boolean; empty?: boolean; error?: string }) => void;
  onReady?: () => void;
  onError?: (error: { message: string; isConfigurationError?: boolean }) => void;
  /** Receives the Apple Pay token, which arrives without a submit of ours. */
  onWalletToken?: (result: any) => void;
};

/**
 * Collect.js inline card fields. Collect.js attaches its own iframes to the
 * containers below by CSS selector — it is imperative and lives outside React,
 * so the only thing this component owns is the DOM to mount into plus the
 * validation state for inline error styling.
 */
export const CardElement: React.FC<Props> = ({
  className,
  placeholders,
  price,
  currency,
  country,
  applePay,
  onChange,
  onReady,
  onError,
  onWalletToken,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const readyRef = useRef(false);
  const validRef = useRef<Record<string, boolean>>({});

  // The handler is rebuilt on every render of the parent form, but `configure()`
  // captures it once — so keep it behind a ref and hand Collect.js a stable
  // wrapper that always reaches the current one.
  const walletTokenRef = useRef(onWalletToken);
  walletTokenRef.current = onWalletToken;

  // Apple Pay can only show subscription terms on v14 or newer. When the SDK reports
  // the device (or the gateway account) is older, we re-configure without the
  // recurring block — otherwise Collect.js throws instead of rendering a button and
  // the user is left with no Apple Pay at all. The token is one-shot in that case.
  const [recurringUnsupported, setRecurringUnsupported] = useState(false);
  const recurringFallbackRef = useRef(false);

  // The same conditions Collect.js validates before building the wallet
  // abstraction: a numeric price and a currency.
  const hasAmount =
    price != null && price !== '' && !Number.isNaN(Number(price)) && !!currency;

  // The iframes die with these DOM nodes, so the next mount has to configure
  // Collect.js again against the fresh containers. Kept apart from the configure
  // effect below, which re-runs on a price change and must NOT reset.
  useEffect(() => () => {
    resetCollectJsConfiguration();
    readyRef.current = false;
    validRef.current = {};
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Wait for a real amount before configuring at all. The payment modal is
    // mounted (hidden) from page load, well before the products arrive, and
    // Collect.js unconditionally tries to build a Google Pay button on every
    // configure — using the top-level price. Without one it logs "Could not create
    // PaymentRequestAbstraction", which is pure noise but very easy to mistake for
    // a real failure. The effect re-runs as soon as the price lands.
    if (!hasAmount) {
      return undefined;
    }

    // Collect.js is injected asynchronously during billing init, so this form can
    // mount before the global exists. Wait for it instead of failing on the first
    // render — nothing retries this, so an early bail-out would leave the fields
    // permanently unconfigured.
    waitForCollectJs()
      .then(() => {
        if (cancelled) {
          return;
        }

        configureCollectJs({
          selectors: {
            ccnumber: `#${FIELD_IDS.ccnumber}`,
            ccexp: `#${FIELD_IDS.ccexp}`,
            cvv: `#${FIELD_IDS.cvv}`,
          },
          placeholders,
          price,
          currency,
          country,
          applePay: applePay
            ? {
              ...applePay,
              ...(recurringUnsupported ? { isRecurringTransaction: false } : {}),
              // Mandatory whenever the recurring block is on: without it the SDK
              // throws on an old Apple Pay instead of telling us about it.
              recurringMismatchCallback: () => {
                if (recurringFallbackRef.current) {
                  return;
                }
                recurringFallbackRef.current = true;
                console.warn(
                  '[TailoredPay] Apple Pay is older than v14 — falling back to a '
                  + 'non-recurring sheet. The wallet token will NOT be reusable.',
                );
                setRecurringUnsupported(true);
              },
            }
            : null,
          onWalletToken: (result: any) => walletTokenRef.current?.(result),
          onFieldsAvailable: () => {
            if (!readyRef.current) {
              readyRef.current = true;
              onReady?.();
            }
          },
          onValidation: (field, isValid, message) => {
            validRef.current[field] = isValid;

            setErrors((prev) => {
              const next = { ...prev };
              if (isValid || !message) {
                delete next[field];
              } else {
                next[field] = message;
              }
              return next;
            });

            const allValid = Object.keys(FIELD_IDS).every(
              (key) => validRef.current[key],
            );

            onChange?.({ valid: allValid, error: isValid ? undefined : message });
          },
        });
      })
      .catch((e: any) => {
        if (cancelled) {
          return;
        }

        // The SDK never showed up — our problem, not the cardholder's, so it is
        // flagged for a silent fallback to the reserve payment system.
        onError?.({
          message: e?.message || 'Failed to init Collect.js',
          isConfigurationError: true,
        });
      });

    return () => {
      cancelled = true;
    };
    // Re-runs when the amount arrives (the modal is mounted before the products
    // load) or when the plan changes. `configureCollectJs` no-ops when nothing
    // actually changed, so this does not rebuild the card iframes needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price, currency, country, hasAmount, recurringUnsupported]);

  const fieldClass = (field: keyof typeof FIELD_IDS) =>
    classNames(classes.field, { [classes.invalid]: !!errors[field] });

  const firstError = Object.values(errors)[0];

  return (
    <div className={classNames(classes.wrapper, className)}>
      <div id={FIELD_IDS.ccnumber} className={fieldClass('ccnumber')} />
      <div className={classes.row}>
        <div id={FIELD_IDS.ccexp} className={fieldClass('ccexp')} />
        <div id={FIELD_IDS.cvv} className={fieldClass('cvv')} />
      </div>
      {!!firstError && <p className={classes.error}>{firstError}</p>}
      <div id={THREE_DS_MOUNT_ID} className={classes.threeDSMount} />
    </div>
  );
};

export default CardElement;
